import { useDebouncedCallback } from '@/hooks/use-debounce';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { SaveStatus } from './save-status-indicator';

const SAVED_INDICATOR_TIMEOUT_MS = 3000;

interface UseDraftAutosaveOptions<T> {
  initial: T;
  /** Persist the draft. Throwing flips the status to 'error'. */
  onSave: (next: T) => Promise<void>;
  debounceMs?: number;
}

export interface UseDraftAutosave<T> {
  draft: T;
  isDirty: boolean;
  status: SaveStatus;
  lastSavedAt: Date | null;
  /** Update the draft and schedule a debounced save (no-op changes are ignored). */
  applyChange: (next: T) => void;
  /** Adopt an already-persisted state without scheduling a save. */
  commitSaved: (next: T) => void;
  /** Force an immediate save of the current draft (retry). */
  saveNow: () => void;
}

// Shared autosave engine for the wizard steps: local draft + debounced save,
// a no-op guard so the spinner never hangs, flush-on-unmount and a
// beforeunload guard. Extracted from Step 1 so Steps 2/4 reuse it verbatim.
export function useDraftAutosave<T>({
  initial,
  onSave,
  debounceMs = 1500,
}: UseDraftAutosaveOptions<T>): UseDraftAutosave<T> {
  const [draft, setDraft] = useState<T>(initial);
  const [lastSaved, setLastSaved] = useState<T>(initial);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [status, setStatus] = useState<SaveStatus>('idle');

  const lastSavedRef = useRef(lastSaved);
  useEffect(() => {
    lastSavedRef.current = lastSaved;
  }, [lastSaved]);

  const draftRef = useRef(draft);
  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  const onSaveRef = useRef(onSave);
  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);

  const isDirty = JSON.stringify(draft) !== JSON.stringify(lastSaved);

  const performSave = useCallback(async (next: T) => {
    if (JSON.stringify(next) === JSON.stringify(lastSavedRef.current)) {
      setStatus((s) => (s === 'saving' ? 'idle' : s));
      return;
    }
    setStatus('saving');
    try {
      await onSaveRef.current(next);
      setLastSaved(next);
      lastSavedRef.current = next;
      setLastSavedAt(new Date());
      setStatus('saved');
    } catch {
      setStatus('error');
    }
  }, []);

  const performSaveRef = useRef(performSave);
  useEffect(() => {
    performSaveRef.current = performSave;
  }, [performSave]);

  const debouncedSave = useDebouncedCallback((next: T) => {
    void performSaveRef.current(next);
  }, debounceMs);

  useEffect(() => {
    if (status !== 'saved') return;
    const timer = setTimeout(() => setStatus('idle'), SAVED_INDICATOR_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [status]);

  useEffect(() => () => debouncedSave.flush(), [debouncedSave]);

  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const applyChange = useCallback(
    (next: T) => {
      setDraft(next);
      if (JSON.stringify(next) === JSON.stringify(lastSavedRef.current)) return;
      setStatus('saving');
      debouncedSave(next);
    },
    [debouncedSave],
  );

  const commitSaved = useCallback((next: T) => {
    setDraft(next);
    setLastSaved(next);
    lastSavedRef.current = next;
    setLastSavedAt(new Date());
    setStatus('saved');
  }, []);

  const saveNow = useCallback(() => {
    void performSaveRef.current(draftRef.current);
  }, []);

  return { draft, isDirty, status, lastSavedAt, applyChange, commitSaved, saveNow };
}
